import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import StatusChip from '../../components/StatusChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  getStockAdjustment,
  authorizeStockAdjustment,
  cancelStockAdjustment,
} from '../../api/stockAdjustment';
import { getStores } from '../../api/stockLedger';
import { toInventoryList } from '../../api/inventory-request';
import { storeDisplayName } from '../../utils/storeEntity';
import { formatDate, formatDateTime } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

function DetailField({ label, children }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block' }}>
        {label}
      </Typography>
      <Typography sx={{ color: IPD_COLORS.textPrimary, fontSize: 14 }}>{children || '—'}</Typography>
    </Box>
  );
}

export default function StockAdjustmentDetail() {
  const { id } = useParams();
  const history = useHistory();
  const { can } = useInvRole();
  const { url } = useRouteMatch();
  const listUrl = url.replace(/\/[^/]+$/, '');

  const [adj, setAdj] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    getStores()
      .then(d => setStores(toInventoryList(d)))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // API: GET /inventory/stock-adjustments/{id}/
      const data = await getStockAdjustment(id);
      setAdj(data);
    } catch (e) {
      setError(e.message);
      setAdj(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function runConfirm() {
    const required = confirmAction === 'authorize'
      ? INV_ACTIONS.ADJUSTMENT_AUTHORIZE
      : INV_ACTIONS.ADJUSTMENT_CANCEL;
    if (!can(required)) return;
    setActing(true);
    try {
      if (confirmAction === 'authorize') {
        // API: POST /inventory/stock-adjustments/{id}/authorize/
        await authorizeStockAdjustment(id);
        toast.success('Adjustment authorized — inventory updated');
      } else {
        // API: DELETE /inventory/stock-adjustments/{id}/
        await cancelStockAdjustment(id);
        toast.success('Adjustment cancelled');
      }
      setConfirmAction(null);
      load();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setActing(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }

  if (error || !adj) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Not found'}</Alert>
        <InvButton variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => history.push(listUrl)}>
          Back
        </InvButton>
      </Box>
    );
  }

  const isDraft = adj.status === 'DRAFT';
  const store = stores.find(s => (s.store_id || s.id) === adj.store_id);
  const lines = Array.isArray(adj.lines) ? adj.lines : [];

  return (
    <Box>
      <PageHeader
        eyebrow="Inventory Management · Stock Adjustments"
        title={adj.adjustment_number || 'Adjustment'}
        description={adj.reason || 'Stock adjustment detail'}
        actions={(
          <>
            <InvButton variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push(listUrl)}>
              Back
            </InvButton>
            {isDraft && (
              <>
                {can(INV_ACTIONS.ADJUSTMENT_CANCEL) && (
                  <InvButton
                    size="small"
                    variant="outlined"
                    tone="red"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setConfirmAction('cancel')}
                  >
                    Cancel
                  </InvButton>
                )}
                {can(INV_ACTIONS.ADJUSTMENT_AUTHORIZE) && (
                  <InvButton
                    size="small"
                    startIcon={<CheckIcon />}
                    onClick={() => setConfirmAction('authorize')}
                  >
                    Authorize
                  </InvButton>
                )}
              </>
            )}
          </>
        )}
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <SectionCard title="Header" actions={<StatusChip status={adj.status} />}>
            <DetailField label="Store">{store ? storeDisplayName(store) : adj.store_id}</DetailField>
            <DetailField label="Reason">{adj.reason}</DetailField>
            <DetailField label="Remarks">{adj.remarks}</DetailField>
            <DetailField label="Created">{formatDateTime(adj.created_at)}</DetailField>
            <DetailField label="Authorized at">{formatDateTime(adj.authorized_at)}</DetailField>
            <DetailField label="Authorized by">{adj.authorized_by}</DetailField>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={8}>
          <SectionCard title="Lines">
            <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Type</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }} align="right">Current</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }} align="right">Qty</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography sx={{ color: IPD_COLORS.textSecondary }}>No lines</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {lines.map((line) => (
                  <TableRow key={line.id || `${line.item_id}-${line.batch_number}`}>
                    <TableCell>{line.item_name || line.item_id || '—'}</TableCell>
                    <TableCell>{line.batch_number || '—'}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 12,
                          color: line.adjustment_type === 'DECREASE'
                            ? IPD_COLORS.warning.text
                            : IPD_COLORS.success.text,
                        }}
                      >
                        {line.adjustment_type || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{line.current_quantity ?? '—'}</TableCell>
                    <TableCell align="right">{line.adjustment_quantity ?? '—'}</TableCell>
                    <TableCell>{formatDate(line.expiry_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SectionCard>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={Boolean(confirmAction)}
        title={confirmAction === 'authorize' ? 'Authorize Adjustment' : 'Cancel Adjustment'}
        description={
          confirmAction === 'authorize'
            ? `Authorize "${adj.adjustment_number}"? Stock will be updated permanently.`
            : `Cancel "${adj.adjustment_number}"?`
        }
        destructive={confirmAction === 'cancel'}
        loading={acting}
        onConfirm={runConfirm}
        onCancel={() => setConfirmAction(null)}
      />
    </Box>
  );
}
